import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Preloader from '../components/Preloader';
import ScrollToTop from '../components/ScrollToTop';
import PageTitle from '../components/PageTitle';
import { useCart } from '../context/CartContext';

export default function ShopCheckout() {
  const { cartItems, cartTotal, clearCart } = useCart();
  const tax = +(cartTotal * 0.08).toFixed(2);
  const grandTotal = (cartTotal + tax).toFixed(2);

  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', zip: '', country: '',
    payment: 'credit',
  });
  const [errors, setErrors] = useState({});
  const [placed, setPlaced] = useState(false);

  const handle = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const e = {};
    if (!form.firstName.trim()) e.firstName = 'Required';
    if (!form.lastName.trim())  e.lastName  = 'Required';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Valid email required';
    if (!form.address.trim())   e.address   = 'Required';
    if (!form.city.trim())      e.city      = 'Required';
    return e;
  };

  const submit = e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setPlaced(true);
    clearCart();
  };

  if (placed) return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <div className="order-success-page">
        <div className="order-success-box">
          <div className="order-success-icon"><i className="fas fa-circle-check"></i></div>
          <h2>Order Placed Successfully!</h2>
          <p>Thank you for your order. A confirmation has been sent to <strong>{form.email}</strong>.</p>
          <p>Our team will contact you shortly with full details.</p>
          <div className="d-flex gap-3 justify-content-center mt-4">
            <Link to="/shop"  className="theme-btn btn-style-one"><span className="btn-title">Continue Shopping</span></Link>
            <Link to="/"      className="theme-btn btn-style-four"><span className="btn-title">Back to Home</span></Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );

  if (cartItems.length === 0) return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle title="Checkout" breadcrumbs={[{ label: 'Shop', path: '/shop' }, { label: 'Checkout' }]} />
      <div className="empty-cart" style={{ padding: '80px 20px', textAlign: 'center' }}>
        <i className="fas fa-cart-shopping empty-cart-icon"></i>
        <h3>Your cart is empty</h3>
        <p>Add some rooms or products before checking out.</p>
        <Link to="/shop" className="theme-btn btn-style-one mt-3"><span className="btn-title">Go to Shop</span></Link>
      </div>
      <Footer />
    </div>
  );

  return (
    <div className="page-wrapper">
      <Preloader />
      <Header />
      <PageTitle title="Checkout" breadcrumbs={[{ label: 'Shop', path: '/shop' }, { label: 'Checkout' }]} />

      <section className="checkout-section pt-80 pb-80">
        <div className="auto-container">
          <form onSubmit={submit} noValidate>
            <div className="row g-4">
              {/* Billing */}
              <div className="col-lg-7">
                <div className="checkout-box">
                  <h4 className="checkout-box-title">Billing Details</h4>
                  <div className="row g-3">
                    {[
                      { name: 'firstName', label: 'First Name *', col: 6, type: 'text' },
                      { name: 'lastName',  label: 'Last Name *',  col: 6, type: 'text' },
                      { name: 'email',     label: 'Email *',      col: 6, type: 'email' },
                      { name: 'phone',     label: 'Phone',        col: 6, type: 'tel' },
                      { name: 'address',   label: 'Address *',    col: 12, type: 'text' },
                      { name: 'city',      label: 'City *',       col: 5, type: 'text' },
                      { name: 'zip',       label: 'ZIP Code',     col: 3, type: 'text' },
                    ].map(f => (
                      <div key={f.name} className={`col-sm-${f.col}`}>
                        <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: 1, fontSize: 11 }}>{f.label}</label>
                        <input
                          name={f.name}
                          type={f.type}
                          className={`form-control${errors[f.name] ? ' is-invalid' : ''}`}
                          value={form[f.name]}
                          onChange={handle}
                        />
                        {errors[f.name] && <div className="invalid-feedback">{errors[f.name]}</div>}
                      </div>
                    ))}
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold text-muted text-uppercase" style={{ letterSpacing: 1, fontSize: 11 }}>Country</label>
                      <select name="country" className="form-select" value={form.country} onChange={handle}>
                        <option value="">Select…</option>
                        <option>United States</option>
                        <option>United Kingdom</option>
                        <option>Nigeria</option>
                        <option>Canada</option>
                        <option>Australia</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Payment */}
                <div className="checkout-box mt-4">
                  <h4 className="checkout-box-title">Payment Method</h4>
                  {[
                    { value: 'credit',   label: 'Credit / Debit Card', icon: 'fas fa-credit-card' },
                    { value: 'paypal',   label: 'PayPal',              icon: 'fab fa-paypal' },
                    { value: 'transfer', label: 'Bank Transfer',       icon: 'fas fa-building-columns' },
                  ].map(m => (
                    <label key={m.value} className="payment-option">
                      <input
                        type="radio"
                        name="payment"
                        value={m.value}
                        checked={form.payment === m.value}
                        onChange={handle}
                      />
                      <i className={m.icon}></i>
                      <span>{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="col-lg-5">
                <div className="cart-summary">
                  <h4 className="cart-summary-title">Your Order</h4>
                  <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}>
                    {cartItems.map(item => (
                      <div key={`${item.id}-${item.type}`} className="checkout-item-row">
                        <img src={item.img} alt={item.title} />
                        <div className="flex-1">
                          <strong>{item.title}</strong>
                          <span className="d-block text-muted" style={{ fontSize: 12 }}>
                            {item.type === 'room' ? '🏨 Room' : '📦 Product'} × {item.qty}
                          </span>
                        </div>
                        <span style={{ fontWeight: 700, color: 'var(--theme-color1)', whiteSpace: 'nowrap' }}>
                          ${(item.price * item.qty).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <ul className="summary-list">
                    <li><span>Subtotal</span><span>${cartTotal.toFixed(2)}</span></li>
                    <li><span>Shipping</span><span className="text-success">Free</span></li>
                    <li><span>Tax (8%)</span><span>${tax}</span></li>
                  </ul>
                  <div className="summary-total">
                    <span>Grand Total</span>
                    <span>${grandTotal}</span>
                  </div>
                  <button type="submit" className="theme-btn btn-style-one w-100 mt-4" style={{ display: 'block', textAlign: 'center' }}>
                    <span className="btn-title">Place Order — ${grandTotal}</span>
                  </button>
                  <div className="cart-secure-note mt-2">
                    <i className="fas fa-lock"></i> Secure Checkout — SSL Encrypted
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
}
